import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TaskRow } from "@/components/TaskRow";
import TaskFormDialog from "@/components/TaskFormDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ListTodo, Plus } from "lucide-react";
import { toast } from "sonner";

export function ClientTasks({ clientId, clientName }) {
  const [tasks, setTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/tasks", { params: { client_id: clientId } }).then((r) => setTasks(r.data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update status"); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/tasks/${deleteTarget.id}`); toast.success("Task deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-tasks-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-gray-400" /> Tasks
        </h3>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="client-new-task-button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
          <Plus className="w-3.5 h-3.5" /> New task
        </button>
      </div>

      {tasks.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-sm" data-testid="client-tasks-empty">No tasks for this client yet.</p>
      ) : (
        <div className="space-y-2" data-testid="client-tasks-list">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} onStatus={onStatus} onEdit={(task) => { setEditing(task); setDialogOpen(true); }} onDelete={setDeleteTarget} showClient={false} />
          ))}
        </div>
      )}

      <TaskFormDialog open={dialogOpen} onOpenChange={setDialogOpen} task={editing}
        fixedClientId={clientId} fixedClientName={clientName} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete task?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.title}</strong> will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-task-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
